import { Link } from "react-router-dom";
import { Clock, Users, CalendarCheck, Accessibility, Euro, Landmark } from "lucide-react";

export const SITE_URL = "https://formactionpro.net";

export const FORMATIONS = [
  {
    to: "/formations/ia-prompt-engineering",
    label: "IA & Prompt Engineering",
    duree: "1 à 3 jours",
    texte:
      "Comprendre les modèles d’IA, maîtriser l’art du prompt et déployer des usages concrets dans un cadre RGPD et AI Act.",
  },
  {
    to: "/formations/automatisation-no-code",
    label: "Automatisation No-code",
    duree: "2 à 4 jours",
    texte:
      "Concevoir et construire des automatisations sans coder pour gagner du temps au quotidien, seul ou en équipe.",
  },
  {
    to: "/formations/hygiene-pms",
    label: "Hygiène alimentaire & PMS",
    duree: "1 à 3 jours",
    texte:
      "Perfectionner les pratiques d’hygiène et structurer le Plan de Maîtrise Sanitaire de votre établissement.",
  },
];

export const QUALITE_LABEL =
  "Démarche de certification qualité en cours ; à son obtention, nos formations seront éligibles aux financements OPCO.";

export const NDA_LABEL =
  "Déclaration d’activité enregistrée sous le numéro 93830854183 auprès du préfet de région Provence-Alpes-Côte d’Azur. Cet enregistrement ne vaut pas agrément de l’État.";

export const Kicker = ({ children, dark = false }) => (
  <p
    className={`font-sans text-xs font-semibold uppercase tracking-[2px] mb-4 ${
      dark ? "text-vert" : "text-vert-dark"
    }`}
  >
    {children}
  </p>
);

export const SectionHead = ({ kicker, title, dark = false, center = true }) => (
  <div className={`${center ? "text-center" : ""} mb-14`}>
    <Kicker dark={dark}>{kicker}</Kicker>
    <h2
      className={`font-serif text-4xl md:text-[42px] leading-tight ${
        dark ? "text-white" : "text-marine"
      }`}
    >
      {title}
    </h2>
  </div>
);

export const Btn = ({ to, href, variant = "primary", children, className = "", testid, external = false }) => {
  const base =
    "inline-flex items-center justify-center gap-2 font-sans font-semibold text-base px-8 py-4 rounded transition-colors duration-200";
  const styles = {
    primary: "bg-vert text-marine hover:bg-vert-hover",
    outlineLight: "border border-white text-white hover:bg-white/10",
    outlineDark: "border border-marine text-marine hover:bg-marine/5",
    dark: "bg-marine text-white hover:bg-marine-light",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        className={cls}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
    </Link>
  );
};

export const BadgeAC = ({ label = "À COMPLÉTER" }) => (
  <span
    data-testid="badge-a-completer"
    className="inline-flex items-center bg-ambre/15 text-ambre border border-ambre/50 text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wide"
  >
    {label}
  </span>
);

export const AlertBox = ({ title, children }) => (
  <div
    data-testid="alert-box"
    className="bg-ambre/10 border-l-4 border-ambre rounded-lg p-6 md:p-8"
  >
    <p className="font-sans font-semibold text-ambre text-sm uppercase tracking-wide mb-3">
      {title}
    </p>
    <div className="font-sans text-base text-marine/85 leading-[1.7] space-y-3">{children}</div>
  </div>
);

export const Modalites = ({ duree, prerequis }) => (
  <div data-testid="modalites-block" className="bg-sable border border-bordure rounded-lg p-8 md:p-10">
    <h3 className="font-serif text-2xl text-marine mb-8">Modalités pratiques</h3>
    <div className="grid md:grid-cols-2 gap-6 font-sans text-[15px] leading-relaxed text-marine/85">
      <div className="flex gap-3">
        <Clock className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Durée :</span> {duree} (indicative, adaptée à
          votre besoin et à votre public).
        </p>
      </div>
      <div className="flex gap-3">
        <Users className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Prérequis :</span> {prerequis}
        </p>
      </div>
      <div className="flex gap-3">
        <CalendarCheck className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Délai d’accès :</span> indicatif de 15 jours
          ouvrés à compter de votre accord.
        </p>
      </div>
      <div className="flex gap-3">
        <Accessibility className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Accessibilité :</span> nos formations sont
          ouvertes aux personnes en situation de handicap. Référent handicap : François
          Schoonheere —{" "}
          <a href="mailto:form.action1pro@gmail.com" className="text-vert-dark hover:underline">
            form.action1pro@gmail.com
          </a>{" "}
          / 07 69 05 76 73.
        </p>
      </div>
      <div className="flex gap-3">
        <Landmark className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Financement :</span> {QUALITE_LABEL}
        </p>
      </div>
      <div className="flex gap-3">
        <Euro className="text-vert-dark shrink-0 mt-0.5" size={20} />
        <p>
          <span className="font-semibold text-marine">Tarif :</span> sur devis — réponse sous 48 h
          ouvrées.
        </p>
      </div>
    </div>
  </div>
);

export const DevisCta = ({ title = "Parlons de votre besoin de formation.", text }) => (
  <section className="bg-marine-deep py-20">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="font-serif text-4xl md:text-[42px] text-white mb-6">{title}</h2>
      <p className="font-sans text-lg text-white/70 mb-10">
        {text ||
          "Décrivez votre besoin : devis personnalisé sous 48 h ouvrées, sans engagement."}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Btn to="/contact" testid="cta-devis-final" variant="primary">
          Demander un devis
        </Btn>
        <Btn to="/faq" testid="cta-faq-final" variant="outlineLight">
          Consulter la FAQ
        </Btn>
      </div>
    </div>
  </section>
);

export const PageHero = ({ kicker, title, subtitle, badge }) => (
  <section className="bg-marine pt-16 pb-20 relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_30%_20%,#7FB432,transparent_55%)]" />
    <div className="max-w-4xl mx-auto px-6 relative">
      <Kicker dark>{kicker}</Kicker>
      <h1 className="font-serif font-semibold text-white text-4xl md:text-5xl leading-[1.1] mb-6">
        {title}
      </h1>
      {subtitle && (
        <p className="font-sans text-lg text-white/80 leading-[1.7] max-w-2xl">{subtitle}</p>
      )}
      {badge && <div className="mt-6">{badge}</div>}
    </div>
  </section>
);
